/**
 * Utilitário centralizado para tratamento de CNPJ.
 *
 * Responsabilidade:
 * - Normalizar CNPJ removendo máscara e caracteres inválidos.
 * - Validar CNPJ numérico e alfanumérico conforme regra atual.
 * - Formatar CNPJ no padrão visual: XX.XXX.XXX/XXXX-XX.
 * - Identificar se o CNPJ informado é numérico ou alfanumérico.
 *
 * Este arquivo não deve conter regras de UI, componentes, inputs ou máscaras
 * de digitação. Para regras visuais de máscara, utilize `cnpjMask.js`.
 *
 * Exemplos de uso:
 *
 * import { normalizarCnpj, validar, formatar, ehAlfanumerico } from '@/util/cnpj'
 *
 * const cnpj = normalizarCnpj('AB.123.456/0001-95')
 * const valido = validar(cnpj)
 * const formatado = formatar(cnpj)
 * const alfanumerico = ehAlfanumerico(cnpj)
 */

const REGEX_NORMALIZAR = /[^A-Za-z0-9]/g
const REGEX_VALIDAR = /^[A-Z0-9]{12}\d{2}$/
const REGEX_FORMATAR = /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})$/
const REGEX_NUMERICO = /^\d{14}$/
const REGEX_REPETIDO = /^(.)\1{13}$/

const valorCaractere = (char) => {
  return char.charCodeAt(0) - 48
}

const calcularDigitoVerificador = (base, pesoInicial) => {
  let soma = 0
  let peso = pesoInicial

  for (let i = 0; i < base.length; i++) {
    soma += valorCaractere(base[i]) * peso
    peso = peso === 2 ? 9 : peso - 1
  }

  const resto = soma % 11

  return resto < 2 ? 0 : 11 - resto
}

/**
 * Normaliza um valor de CNPJ.
 *
 * Remove caracteres que não sejam letras ou números e converte letras para maiúsculas.
 *
 * @param {string|number|null|undefined} valor
 * @returns {string|null} CNPJ normalizado ou null quando não houver valor útil.
 */
export const normalizarCnpj = (valor) => {
  if (valor === null || valor === undefined) {
    return null
  }

  const cnpj = valor.toString().replace(REGEX_NORMALIZAR, '').toUpperCase()

  return cnpj === '' ? null : cnpj
}

/**
 * Formata um CNPJ normalizado ou mascarado.
 *
 * @param {string|number|null|undefined} valor
 * @returns {string|null} CNPJ formatado, valor normalizado original ou null.
 */
export const formatar = (valor) => {
  const cnpj = normalizarCnpj(valor)

  if (!cnpj) {
    return null
  }

  const match = cnpj.match(REGEX_FORMATAR)

  if (!match) {
    return cnpj
  }

  return `${match[1]}.${match[2]}.${match[3]}/${match[4]}-${match[5]}`
}

/**
 * Verifica se o CNPJ possui apenas números.
 *
 * @param {string|number|null|undefined} valor
 * @returns {boolean}
 */
export const ehNumerico = (valor) => {
  const cnpj = normalizarCnpj(valor)

  return !!cnpj && REGEX_NUMERICO.test(cnpj)
}

/**
 * Verifica se o CNPJ possui letras na base alfanumérica.
 *
 * Os dois últimos caracteres devem ser numéricos, pois representam
 * os dígitos verificadores.
 *
 * @param {string|number|null|undefined} valor
 * @returns {boolean}
 */
export const ehAlfanumerico = (valor) => {
  const cnpj = normalizarCnpj(valor)

  return !!cnpj && REGEX_VALIDAR.test(cnpj) && /[A-Z]/.test(cnpj.substring(0, 12))
}

/**
 * Valida um CNPJ numérico ou alfanumérico.
 *
 * A validação considera:
 * - tamanho de 14 caracteres;
 * - primeiros 12 caracteres alfanuméricos;
 * - dois últimos caracteres numéricos;
 * - rejeição de sequência repetida;
 * - cálculo dos dígitos verificadores.
 *
 * @param {string|number|null|undefined} valor
 * @returns {boolean}
 */
export const validar = (valor) => {
  const cnpj = normalizarCnpj(valor)

  if (!cnpj) {
    return false
  }

  if (!REGEX_VALIDAR.test(cnpj)) {
    return false
  }

  if (REGEX_REPETIDO.test(cnpj)) {
    return false
  }

  const digito1 = calcularDigitoVerificador(cnpj.substring(0, 12), 5)

  if (Number(cnpj[12]) !== digito1) {
    return false
  }

  const digito2 = calcularDigitoVerificador(cnpj.substring(0, 13), 6)

  return Number(cnpj[13]) === digito2
}

export default {
  normalizarCnpj,
  formatar,
  validar,
  ehNumerico,
  ehAlfanumerico
}
